/**
 * 配送依頼一覧からマスタ詳細画面へのリンク先。
 *
 * 詳細画面のパスパラメータ名がマスタごとに違う（customerId / partnerId / subPartnerId）ため、
 * `to` の union と単一の `params` では TanStack Router のルート別 params 型を満たせない。
 * `kind` で判別できる形で返し、描画側がルートごとに `Link` を書く。
 */
export type MasterDetailLinkTarget =
  | { kind: 'customer'; params: { tenantKey: string; customerId: number } }
  | { kind: 'partner'; params: { tenantKey: string; partnerId: number } }
  | {
      kind: 'subPartner'
      params: { tenantKey: string; subPartnerId: number }
    }

export type MasterDetailLinkKind = MasterDetailLinkTarget['kind']

/** パートナーの種別。API の応答（PartnerEntity / DispatchPartnerExEntity の type）と同じ値 */
type PartnerType = 'IN_HOUSE' | 'CONTRACTOR'

type Options = {
  tenantKey: string
  /**
   * kind が partner のときのパートナー種別。
   * パートナー詳細は取引先（CONTRACTOR）用の画面で、自社（IN_HOUSE）を開くと取引先として描画される。
   * 自社の情報は「拠点」の画面（/settings/in-house）で扱うため、リンク先を返すのは取引先のときだけにする。
   * 種別が取れないとき（FE が先にデプロイされ、API がまだ type を返さない間など）も、
   * 自社かどうか分からないのでリンク先を返さない
   */
  partnerType?: PartnerType | null
  /**
   * マスタ詳細を開けるか。配送依頼一覧を開けるのは OWNER だけで、OWNER はマスタを閲覧できるため
   * 既定は true。一覧を開けるユーザー種別が増えたら、呼び出し側で判定して渡す
   */
  canOpenMasterDetail?: boolean
}

/**
 * マスタ詳細へのリンク先を返す。
 * ID が無い、または閲覧できない場合は null を返し、呼び出し側は通常のテキストとして描画する。
 */
export const resolveMasterDetailLink = (
  kind: MasterDetailLinkKind,
  id: number | null | undefined,
  { tenantKey, partnerType, canOpenMasterDetail = true }: Options
): MasterDetailLinkTarget | null => {
  // ID は採番された正の整数。0 は存在しない ID なので、無い場合と同じに扱う
  if (!id || !canOpenMasterDetail) return null

  switch (kind) {
    case 'customer':
      return { kind, params: { tenantKey, customerId: id } }
    case 'partner':
      if (partnerType !== 'CONTRACTOR') return null
      return { kind, params: { tenantKey, partnerId: id } }
    case 'subPartner':
      return { kind, params: { tenantKey, subPartnerId: id } }
  }
}
